import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import ReactHtmlParser from "react-html-parser";

import Tippy from "@tippyjs/react";
import StyledButton from "@stadium/ui/src/components/button/styled-button";
import Multimedia from "@sharedComponents/multimedia";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { addClassToBody, removeClassFromBody } from "@stadium/helpers/dom";

import useModifyUser from "@stadium/hooks/lib/use-modify-user";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";

import styles from "./main.module.scss";

const onTippyMount = (el) => el?.reference.scrollIntoView(false);

const useDemoTour = ({ guideTourSteps, userConfigTourPersistence } = {}) => {
  const [step, setStep] = useState(null);
  const [showGuideTour, setShowGuideTour] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { replace, asPath } = useRouter();

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const { currentUserConfig, refetchUserConfigData } = useCurrentUserContext();
  const { modifyUser } = useModifyUser(() => refetchUserConfigData?.());

  const firstTimeVisit = useMemo(() => {
    if (userConfigTourPersistence?.viewedUserConfigField) {
      return !currentUserConfig?.[userConfigTourPersistence.viewedUserConfigField];
    }
    return true;
  }, [userConfigTourPersistence, currentUserConfig]);

  const onCloseGuideTourCallback = useCallback(() => {
    if (userConfigTourPersistence?.modifyUserInput) {
      modifyUser({ variables: { input: userConfigTourPersistence.modifyUserInput } });
    }
  }, [userConfigTourPersistence, modifyUser]);

  const guideTourStepsArray = Object.values(guideTourSteps);
  const totalSteps = guideTourStepsArray.length;
  const isLastStep = step == totalSteps;

  useEffect(() => {
    if (isMobileDevice) {
      return;
    }

    setIsLoading(true);
  }, [asPath, isMobileDevice]);

  const onDemoNextBtn = (e) => {
    e.preventDefault();
    if (guideTourStepsArray[step]?.shouldReinitialize) {
      setShowGuideTour(false);
    }
    setStep(isLastStep ? null : step + 1);

    const nextPageUrl = guideTourStepsArray[step - 1]?.nextPageUrl;
    if (!isMobileDevice && nextPageUrl) {
      replace(nextPageUrl);
    }

    removeClassFromBody(guideTourStepsArray[step - 1].targetClass);
    if (!isLastStep) {
      addClassToBody(guideTourStepsArray[step]?.targetClass);
    }
    if (isLastStep) {
      removeClassFromBody("guide-tour-start");
      firstTimeVisit && onCloseGuideTourCallback();
    }
  };

  const closeGuideTour = useCallback(() => {
    const stepsArr = Object.values(guideTourSteps);
    const currentStep = stepsArr.find(({ id }) => id == step);
    currentStep && removeClassFromBody(currentStep.targetClass);
    step && removeClassFromBody("guide-tour-start");
    if (firstTimeVisit) {
      onCloseGuideTourCallback();
    }
    setShowGuideTour(false);
    setStep(null);
  }, [step, guideTourSteps, isLastStep, firstTimeVisit, onCloseGuideTourCallback]);

  const renderTooltips = () =>
    guideTourStepsArray.map(
      (
        {
          name,
          ref,
          placement,
          imgSrc,
          title,
          textData,
          primaryActionText,
          primaryCTA,
          secondaryActionText,
          secondaryCTA,
          nextCtaText,
          hideNextCta,
          showCloseCta,
          showNewTag,
          isResponsiveModal,
          tooltipOffset,
          contentContainerClass = "",
          hideTourImage = false,
        },
        idx,
      ) => {
        const stepNo = idx + 1;
        const isLastStep = step == totalSteps;
        const nextText = nextCtaText || (isLastStep ? "DONE" : "NEXT");
        const isVisible = stepNo === step;

        if (isMobileDevice && isResponsiveModal) {
          return (
            isVisible && (
              <div key={`demo-${name}-${isLoading}`} className={styles.mobileGuideTour}>
                <div className={styles.stepSkipHeader}>
                  <span className={styles.stepsCounter}>
                    {stepNo}/{totalSteps}
                  </span>
                  <StyledButton
                    styleType="link"
                    onClick={closeGuideTour}
                    className={styles.closeGuideTour}
                  ></StyledButton>
                </div>

                <div className={styles.tourTitle}>{title}</div>
                <div className={styles.imgHolder}>
                  <Multimedia
                    type="image"
                    alt="tour-image"
                    src={imgSrc}
                    className={styles.imgPhone}
                  />
                </div>
                <div className={styles.imgCaption}>{textData}</div>
                <StyledButton onClick={onDemoNextBtn} className={styles.tourBtn}>
                  {primaryActionText || nextText}
                </StyledButton>
              </div>
            )
          );
        }

        if (ref.current) {
          return (
            <Tippy
              key={`demo-${name}`}
              onMount={onTippyMount}
              onClickOutside={closeGuideTour}
              content={
                <div
                  className={`${styles.guideTip} ${contentContainerClass} ${
                    hideTourImage ? styles.guideTipFooterCart : ""
                  }`}
                >
                  <div className={`${styles.stepSkipHeader} step-skip-Header`}>
                    <span className={styles.stepsCounter}>
                      {stepNo}/{totalSteps}
                    </span>
                    {!hideNextCta && !showCloseCta && (
                      <StyledButton
                        onClick={onDemoNextBtn}
                        styleType="link"
                        className={styles.skipLink}
                      >
                        {nextText}
                      </StyledButton>
                    )}
                    {showCloseCta && (
                      <StyledButton
                        onClick={closeGuideTour}
                        className={styles.closeGuideTour}
                      ></StyledButton>
                    )}
                  </div>

                  <div className={styles.guideTourBody}>
                    {!hideTourImage && (
                      <div className={styles.stepImgArea}>
                        <Multimedia type="image" width={80} height={80} src={imgSrc} />
                      </div>
                    )}

                    <div className={styles.stepInfoText}>
                      <div className={styles.stepTitle}>
                        {title} {showNewTag && <span className={styles.newTag}>New!</span>}
                      </div>
                      <p>{ReactHtmlParser(textData)}</p>
                    </div>
                  </div>

                  {primaryActionText && (
                    <div className={`${styles.guideTourAction} guide-tour-action`}>
                      {secondaryActionText && (
                        <StyledButton styleType="primary-outline" onClick={secondaryCTA}>
                          {secondaryActionText}
                        </StyledButton>
                      )}

                      <StyledButton onClick={primaryCTA || onDemoNextBtn} target>
                        {primaryActionText}
                      </StyledButton>
                    </div>
                  )}

                  {!hideNextCta && showCloseCta && (
                    <div className={styles.bottomCtaBlock}>
                      <button onClick={onDemoNextBtn} className={styles.skipLink}>
                        {nextText}
                      </button>
                    </div>
                  )}
                </div>
              }
              arrow
              reference={ref}
              placement={placement}
              offset={tooltipOffset}
              interactive
              className={`${styles.guideToolTip} ${"stepCount" + stepNo} ${
                isLastStep ? styles.lastStepPosition : styles.commonStepPosition
              }`}
              visible={step == stepNo}
            />
          );
        }
      },
    );

  const renderTour = () => {
    return step ? (
      <React.Fragment key={isLoading}>
        <div className={`guide-tour-overlay ${styles.guideTourOverlay}`} />
        {showGuideTour && renderTooltips()}
      </React.Fragment>
    ) : null;
  };

  return {
    guideTourSteps,
    setStep,
    step,
    renderTour,
    showGuideTour,
    setShowGuideTour,
    closeGuideTour,
    setIsLoading,
  };
};

export default useDemoTour;
